import React, { useCallback, useEffect, useRef, useState } from "react"

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  toast,
  Tooltip,
} from "@coval/ui"
import { Check, ChevronDown, Copy, Loader2, Plug, XCircle } from "lucide-react"

import type { TestAgentConnectionResponse } from "@/types_api/backend/transport/test_agent_connection_response"
import { cn } from "@/utils/cn"

import { useAgentTestConnectionTransport } from "../transport/hooks/useAgentsTransport"

type TestConnectionResponse = TestAgentConnectionResponse

const AUTO_DISMISS_SUCCESS_MS = 60_000
const COPY_FEEDBACK_MS = 1_500

export interface AgentTestConnectionConfig {
  agentId: string
  disabled: boolean
  disabledReason?: string
}

type AgentTestConnectionButtonProps = AgentTestConnectionConfig

async function copyToClipboard(
  value: string,
  successTitle?: string,
): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value)
    if (successTitle) {
      toast.success({ title: successTitle })
    }
    return true
  } catch {
    toast.danger({ title: "Copy failed" })
    return false
  }
}

const InlineCopyButton: React.FC<{ value: string; label: string }> = ({
  value,
  label,
}) => {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    },
    [],
  )

  const onCopy = useCallback(async () => {
    const ok = await copyToClipboard(value)
    if (!ok) {
      return
    }
    setCopied(true)
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    timerRef.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS)
  }, [value])

  return (
    <Tooltip content={copied ? "Copied" : label} side="top">
      <button
        type="button"
        onClick={() => void onCopy()}
        aria-label={label}
        className="inline-flex size-6 items-center justify-center rounded text-coval-text-muted hover:bg-coval-contrast-hover hover:text-coval-text transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-current"
      >
        {copied ? (
          <Check className="size-3 text-coval-success-text" />
        ) : (
          <Copy className="size-3" />
        )}
      </button>
    </Tooltip>
  )
}

interface FieldRowProps {
  label: string
  value: string | number | boolean | null | undefined
  copyable?: boolean
  mono?: boolean
}

const FieldRow: React.FC<FieldRowProps> = ({
  label,
  value,
  copyable = true,
  mono = false,
}) => {
  if (value === undefined || value === null || value === "") {
    return null
  }
  const display = String(value)
  return (
    <div className="grid grid-cols-[140px_minmax(0,1fr)_auto] items-start gap-2 border-b border-coval-border py-1.5 last:border-b-0">
      <div className="pt-0.5 text-xs font-medium text-coval-text-muted">
        {label}
      </div>
      <div
        className={cn("break-all text-xs text-coval-text", mono && "font-mono")}
      >
        {display}
      </div>
      <div className="flex items-center">
        {copyable && (
          <InlineCopyButton value={display} label={`Copy ${label}`} />
        )}
      </div>
    </div>
  )
}

interface DetailSectionProps {
  title: string
  raw: unknown
  children: React.ReactNode
  defaultOpen?: boolean
}

const DetailSection: React.FC<DetailSectionProps> = ({
  title,
  raw,
  children,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen)
  const json = JSON.stringify(raw, null, 2)
  return (
    <div className="rounded-md border border-coval-border bg-coval-surface-raised">
      <div className="flex items-center justify-between border-b border-coval-border px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-coval-text-muted transition-colors hover:text-coval-text"
          aria-expanded={open}
          aria-label={`${open ? "Hide" : "Show"} ${title}`}
        >
          <ChevronDown
            className={cn("size-3 transition-transform", !open && "-rotate-90")}
          />
          {title}
        </button>
        <Tooltip content={`Copy ${title} as JSON`} side="top">
          <button
            type="button"
            onClick={() =>
              void copyToClipboard(json, `${title} copied as JSON`)
            }
            aria-label={`Copy ${title} as JSON`}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-coval-text-muted transition-colors hover:bg-coval-contrast-hover hover:text-coval-text"
          >
            <Copy className="size-3" />
            Copy JSON
          </button>
        </Tooltip>
      </div>
      {open && <div className="px-3 py-1">{children}</div>}
    </div>
  )
}

export const AgentTestConnectionButton: React.FC<
  AgentTestConnectionButtonProps
> = ({ agentId, disabled, disabledReason }) => {
  const [result, setResult] = useState<TestConnectionResponse | null>(null)
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current)
      dismissTimerRef.current = null
    }
  }, [])

  const testConnectionMutation = useAgentTestConnectionTransport({
    onSuccess: (data: TestConnectionResponse) => {
      setResult(data)
      clearDismissTimer()
      if (data.status === "success") {
        dismissTimerRef.current = setTimeout(
          () => setResult(null),
          AUTO_DISMISS_SUCCESS_MS,
        )
      }
    },
  })

  useEffect(() => clearDismissTimer, [clearDismissTimer])

  const handleTest = useCallback(() => {
    if (!agentId) {
      return
    }
    setResult(null)
    clearDismissTimer()
    testConnectionMutation.mutate({ agent_id: agentId })
  }, [agentId, clearDismissTimer, testConnectionMutation])

  const handleDismiss = useCallback(() => {
    clearDismissTimer()
    setResult(null)
    testConnectionMutation.reset()
  }, [clearDismissTimer, testConnectionMutation])

  const isPending = testConnectionMutation.isPending
  const errorMessage = testConnectionMutation.error?.message ?? null
  const hasRequestError = !result && !!errorMessage
  const isSuccess = result?.status === "success"

  const button = (
    <Button
      type="button"
      variant="secondary"
      onClick={handleTest}
      disabled={disabled || isPending}
      aria-label="Test connection"
      aria-busy={isPending}
    >
      {isPending ? (
        <>
          <Loader2 className="size-4 animate-spin" />
          Testing connection&hellip;
        </>
      ) : (
        <>
          <Plug className="size-4" />
          Test connection
        </>
      )}
    </Button>
  )

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {disabled && disabledReason ? (
          <Tooltip content={disabledReason} side="top">
            <span className="inline-flex">{button}</span>
          </Tooltip>
        ) : (
          button
        )}
        {result && (
          <Badge
            variant={isSuccess ? "success" : "danger"}
            className="flex items-center gap-1"
          >
            {isSuccess ? (
              <Check className="size-3" />
            ) : (
              <XCircle className="size-3" />
            )}
            {isSuccess ? "Success" : "Failed"}
            <span className="ml-1 opacity-70">
              {result.duration_ms.toFixed(0)} ms
            </span>
          </Badge>
        )}
      </div>

      {hasRequestError && (
        <Alert variant="danger" onDismiss={handleDismiss}>
          <AlertTitle>Request failed</AlertTitle>
          <AlertDescription className="break-words">
            {errorMessage}
          </AlertDescription>
        </Alert>
      )}

      {result && (
        <Alert variant={isSuccess ? "success" : "danger"} className="flex-col">
          <div className="flex items-start justify-between gap-2">
            <div>
              <AlertTitle>
                {isSuccess
                  ? "Connection handshake succeeded"
                  : "Connection handshake failed"}
              </AlertTitle>
              {result.response.raw_error && (
                <AlertDescription className="break-words">
                  {result.response.raw_error}
                </AlertDescription>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Tooltip content="Copy full result as JSON" side="top">
                <button
                  type="button"
                  onClick={() =>
                    void copyToClipboard(
                      JSON.stringify(result, null, 2),
                      "Full result copied",
                    )
                  }
                  aria-label="Copy full result"
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs opacity-80 transition-opacity hover:opacity-100"
                >
                  <Copy className="size-3" />
                  Copy all
                </button>
              </Tooltip>
              <button
                type="button"
                onClick={handleDismiss}
                className="px-1 text-xs opacity-70 transition-opacity hover:opacity-100"
                aria-label="Dismiss"
              >
                Dismiss
              </button>
            </div>
          </div>

          <div className="mt-3 space-y-3">
            <DetailSection title="Request" raw={result.request}>
              <FieldRow label="Transport" value={result.request.transport} />
              <FieldRow label="Endpoint" value={result.request.endpoint} mono />
              <FieldRow
                label="Authorization"
                value={result.request.authorization_header_preview}
                mono
              />
              <FieldRow
                label="Init payload"
                value={
                  result.request.init_payload_size_bytes !== undefined
                    ? `${result.request.init_payload_size_bytes} bytes`
                    : undefined
                }
                copyable={false}
              />
              <FieldRow
                label="Expected ready"
                value={result.request.expected_ready_message_type}
                mono
              />
            </DetailSection>

            <DetailSection title="Response" raw={result.response}>
              <FieldRow
                label="Ready received"
                value={result.response.ready_message_received}
                copyable={false}
              />
              <FieldRow
                label="Message types"
                value={result.response.received_message_types?.join(", ")}
                mono
              />
              <FieldRow
                label="HTTP status"
                value={result.response.http_status ?? undefined}
                copyable={false}
              />
              <FieldRow
                label="Content-Type"
                value={result.response.response_content_type ?? undefined}
                mono
              />
              <FieldRow
                label="Body preview"
                value={result.response.response_body_preview ?? undefined}
                mono
              />
              <FieldRow
                label="Raw error"
                value={result.response.raw_error ?? undefined}
                mono
              />
            </DetailSection>
          </div>
        </Alert>
      )}
    </div>
  )
}
